import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertCircle, Loader2, Snowflake } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

const labelForType = (t) =>
  t === "silvester" ? "Silvester-Tickets" : t === "eisstock" ? "Eisstockschießen" : "Gutschein";

export default function PayPage() {
  const { type, id } = useParams();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/payment-link/${type}/${id}`);
        if (alive) {
          setDetails(res.data);
          if (res.data.payment_status === "completed") setCompleted(true);
        }
      } catch (e) {
        if (alive) setError(e.response?.data?.detail || "Bestellung nicht gefunden");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [type, id]);

  const createOrder = async () => {
    try {
      const res = await axios.post(`${API}/paypal/create-order`, {
        booking_id: id,
        booking_type: type,
      });
      return res.data.id;
    } catch (e) {
      toast.error("Fehler bei der PayPal-Bestellung");
      throw e;
    }
  };

  const onApprove = async (data) => {
    try {
      await axios.post(`${API}/paypal/capture-order`, {
        order_id: data.orderID,
        booking_id: id,
        booking_type: type,
      });
      setCompleted(true);
      toast.success("Zahlung erfolgreich!");
    } catch (e) {
      toast.error("Fehler bei der Zahlungsabwicklung");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <Loader2 className="w-8 h-8 text-sky-500 animate-spin" data-testid="pay-loading" />
      </div>
    );
  }

  if (error || !details) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <Card className="max-w-md w-full border-0 shadow-xl">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <h1 className="text-xl font-bold text-slate-900 mb-2">Bestellung nicht verfügbar</h1>
            <p className="text-slate-600 mb-4">{error || "Diese Bestellung existiert nicht oder ist abgelaufen."}</p>
            <Link to="/"><Button variant="outline">Zur Startseite</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-sky-50 flex items-center justify-center p-6">
        <Card className="max-w-md w-full border-0 shadow-xl" data-testid="pay-success-card">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Vielen Dank!</h1>
            <p className="text-slate-600 mb-4">Deine Zahlung ist eingegangen. Du erhältst die Bestätigung mit PDF in Kürze per E-Mail.</p>
            <Link to="/"><Button>Zur Startseite</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Helper to render summary
  const total = (details.total_price || 0) + (details.service_fee || 0);
  let summary;
  if (details.type === "silvester") {
    const seats = (details.seat_numbers || []).slice().sort((a, b) => a - b);
    const seatRange = seats.length === 0 ? "–"
      : (seats.length > 1 && seats[seats.length - 1] - seats[0] === seats.length - 1)
        ? `${seats[0]}–${seats[seats.length - 1]}` : seats.join(", ");
    summary = (
      <>
        <li><span className="text-slate-500">Ticket-Typ</span><span className="font-semibold">{details.ticket_type === "vip" ? "VIP" : "Normal"}</span></li>
        <li><span className="text-slate-500">Anzahl</span><span className="font-semibold">{details.ticket_count}</span></li>
        {details.ticket_type === "vip" && seats.length > 0 && (
          <li><span className="text-slate-500">Sitzplätze</span><span className="font-semibold text-amber-700">{seatRange}</span></li>
        )}
        <li><span className="text-slate-500">Datum</span><span className="font-semibold">31. Dezember 2026</span></li>
      </>
    );
  } else if (details.type === "eisstock") {
    summary = (
      <>
        <li><span className="text-slate-500">Datum</span><span className="font-semibold">{details.booking_date}</span></li>
        <li><span className="text-slate-500">Zeit</span><span className="font-semibold">{details.time_slot} Uhr</span></li>
        <li><span className="text-slate-500">Bahn</span><span className="font-semibold">Bahn {details.lane}</span></li>
        <li><span className="text-slate-500">Personen</span><span className="font-semibold">{details.group_size}</span></li>
      </>
    );
  } else {
    summary = (
      <>
        <li><span className="text-slate-500">Anzahl</span><span className="font-semibold">{details.quantity} Gutschein(e)</span></li>
        <li><span className="text-slate-500">Wert pro Gutschein</span><span className="font-semibold">{Number(details.amount_per_voucher).toFixed(2)} €</span></li>
        {details.recipient_name && (
          <li><span className="text-slate-500">Empfänger</span><span className="font-semibold">{details.recipient_name}</span></li>
        )}
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-sky-50 py-12 px-4">
      <div className="max-w-xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 text-sm">
          <Snowflake className="w-4 h-4" /> Grömitzer Eiszeit
        </Link>
        <Card className="border-0 shadow-xl">
          <CardContent className="p-8">
            <h1 className="text-2xl font-bold text-slate-900 mb-1">Bezahlung</h1>
            <p className="text-slate-600 mb-6">
              Hallo {details.name || "lieber Gast"}, deine {labelForType(details.type)}-Bestellung ist vorbereitet.
            </p>

            <div className="bg-slate-50 rounded-2xl p-5 mb-6" data-testid="pay-summary">
              <ul className="space-y-2 [&_li]:flex [&_li]:justify-between">
                {summary}
                <li className="border-t border-slate-200 pt-2 mt-2">
                  <span className="text-slate-500">Servicegebühr</span>
                  <span className="font-semibold">{Number(details.service_fee).toFixed(2)} €</span>
                </li>
                <li>
                  <span className="text-base font-semibold text-slate-900">Gesamtpreis</span>
                  <span className="text-xl font-bold text-sky-600">{total.toFixed(2)} €</span>
                </li>
              </ul>
            </div>

            <div data-testid="pay-paypal-buttons">
              <PayPalScriptProvider
                options={{
                  "client-id": PAYPAL_CLIENT_ID,
                  currency: "EUR",
                  intent: "capture",
                }}
              >
                <PayPalButtons
                  style={{ layout: "vertical", shape: "rect", color: "blue" }}
                  createOrder={createOrder}
                  onApprove={onApprove}
                />
              </PayPalScriptProvider>
            </div>

            <p className="text-xs text-slate-500 mt-6 text-center">
              Sichere Zahlung über PayPal · Bestellung gültig 72&nbsp;Stunden
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
